import { useState, useEffect, useRef, useCallback } from "react";
import { getDefaultComponentConfig } from "../../constants/constants.js";
import { updateComponentConfig as applyLocalComponentConfig } from "../../hooks/adminStore.js";

const CONFIG_LOAD_TIMEOUT_MS = 6000;

export default function useComponentConfig(session, showMsg) {
  const [compConfig, setCompConfig] = useState(null);
  const [saving, setSaving] = useState(false);
  const [configLoading, setConfigLoading] = useState(true);
  const timeoutRef = useRef(null);
  const sessionId = session?.sessionId;

  useEffect(() => {
    if (!session) return;
    if (session.data?.setting) {
      setCompConfig(session.data.setting);
      setConfigLoading(false);
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
      } else if (!timeoutRef.current) {
        timeoutRef.current = setTimeout(() => {
          timeoutRef.current = null;
          setCompConfig((prev) => prev || getDefaultComponentConfig());
          setConfigLoading(false);
        }, CONFIG_LOAD_TIMEOUT_MS);
      }
      // `session` alone covers id + setting: the effect body guards the
      // setting/timeout branches so identity-only churn bails out safely.
    }, [session]);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
        // The ref must be cleared too: a stale non-null handle blocks the
        // `else if (!timeoutRef.current)` guard above from ever re-arming,
        // which left configLoading stuck at true after a session change.
        timeoutRef.current = null;
      }
    };
  }, [session?.sessionId]);

  const handleCompToggle = useCallback(
    (section, fieldKey) => {
      try {
        if (!section || typeof fieldKey !== "string") {
          console.warn("[useComponentConfig] invalid toggle args", section, fieldKey);
          return;
        }
        const prev = compConfig;
        if (!prev || typeof prev !== "object") {
          console.warn("[useComponentConfig] no config to toggle");
          return;
        }
        let next;
        if (section.flat) {
          if (!(fieldKey in prev) && !(fieldKey in getDefaultComponentConfig())) {
            console.warn("[useComponentConfig] unknown flat key", fieldKey);
            return;
          }
          const cur = prev[fieldKey] === true;
          next = { ...prev, [fieldKey]: !cur };
        } else {
          if (!section.key || typeof section.key !== "string") return;
          const cur = prev[section.key]?.[fieldKey] === true;
          next = { ...prev, [section.key]: { ...(prev[section.key] || {}), [fieldKey]: !cur } };
        }
        // Pure state update first, store/publish afterwards - never from inside the
        // updater: notifying subscribers while another component renders can be dropped,
        // which left the switches looking stuck until a refresh.
        setCompConfig(next);
        const id = sessionId;
        if (!id) {
          console.warn("[useComponentConfig] missing sessionId for publish");
          showMsg?.("No session — will apply on Save", "error");
          return;
        }
        Promise.resolve(applyLocalComponentConfig(id, next)).catch((e) => {
          console.warn("[useComponentConfig] publish failed:", e?.message || e);
          showMsg?.("Failed to sync, will retry on Save", "error");
        });
      } catch (e) {
        console.error("[useComponentConfig] handleCompToggle error", e);
      }
    },
    [compConfig, sessionId, showMsg]
  );

  function resetComponents() {
    try {
      if (!window.confirm("Are you sure you want to reset all components?")) return;
      const defaults = getDefaultComponentConfig();
      if (!defaults || typeof defaults !== "object") {
        console.error("[useComponentConfig] defaults invalid");
        return;
      }
      setCompConfig(defaults);
      const id = session?.sessionId;
      if (id) {
        try {
          applyLocalComponentConfig(id, defaults);
          showMsg?.("Components reset to defaults");
        } catch (e) {
          console.warn("[useComponentConfig] reset publish failed", e);
          showMsg?.("Components reset to defaults (click Save Changes to apply)");
        }
      } else {
        showMsg?.("Components reset to defaults (click Save Changes to apply)");
      }
    } catch (e) {
      console.error("[useComponentConfig] reset failed", e);
    }
  }

  const saveComponentConfig = async (sessionId, config) => {
    try {
      const id = sessionId ?? session?.sessionId;
      const cfg = config ?? compConfig;
      if (!id) {
        console.warn("[useComponentConfig] save missing id");
        showMsg?.("No session to save", "error");
        return;
      }
      if (!cfg || typeof cfg !== "object") {
        console.warn("[useComponentConfig] save missing config");
        showMsg?.("Nothing to save", "error");
        return;
      }
      setSaving(true);
      try {
        await Promise.resolve(applyLocalComponentConfig(id, cfg));
        showMsg?.("Component settings saved!");
      } catch (e) {
        console.error("[useComponentConfig] save failed", e);
        showMsg?.("Save failed: " + (e?.message || "unknown"), "error");
      } finally {
        setTimeout(() => setSaving(false), 300);
      }
    } catch (e) {
      console.error("[useComponentConfig] save error", e);
      setSaving(false);
    }
  };

  return { compConfig, configLoading, saving, handleCompToggle, resetComponents, updateComponentConfig: saveComponentConfig };
}
